/**
 * paypal-connect-api — typed wrappers for the ADMIN PayPal onboarding ops,
 * through the @v0y4ge/api-client `apiRaw` choke point (wf-api pattern).
 * Declared REQ-045e; served by the harness __PAYPAL_SEED_LAYER__ until they
 * land in @v0y4ge/api-client / api-mocks.
 *
 * Rules: the FE never builds a PayPal URL — `startPayPalConnect()` returns the
 * backend-minted `redirectUrl` and the browser navigates to it verbatim. The
 * ONLY source of connected-ness is `getPayPalConnection()`; merchantId/currency
 * render only from its response. `status` becomes "verified" via a backend
 * webhook AFTER onboarding (not instant) — callers poll until terminal.
 */
import { apiRaw } from "@v0y4ge/api-client";
import { reportOpSource } from "../../shell/opSource";

const tag = <T,>(op: string, p: Promise<T>): Promise<T> =>
  p.then((r) => { reportOpSource(op, r); return r; });

export type PayPalConnectionStatus = "not_connected" | "connected_unconfirmed" | "verified";

export interface PayPalConnection {
  status: PayPalConnectionStatus;
  merchantId?: string | null;   // display comes ONLY from here, never typed/baked
  currency?: string | null;
  paymentsReceivable?: boolean;
  primaryEmailConfirmed?: boolean;
}

export const startPayPalConnect = () =>
  tag("startPayPalConnect", apiRaw<{ redirectUrl: string }>(
    "/v1/admin/integrations/paypal/connect/start", { method: "POST" }, 'startPayPalConnect'));

export const getPayPalConnection = () =>
  tag("getPayPalConnection", apiRaw<PayPalConnection>(
    "/v1/admin/integrations/paypal/connection"));

/** Recent connection-lifecycle events for the "Recent activity" panel (merged
 *  #164: getPayPalActivity). BARE { activity: [{ at, e }] } — same shape the real
 *  route (routes/paypal.ts) and the mock emit; empty until connected. */
export const getPayPalActivity = () =>
  tag("getPayPalActivity", apiRaw<{ activity: { at: string; e: string }[] }>(
    "/v1/admin/integrations/paypal/activity"));

export const disconnectPayPal = () =>
  tag("disconnectPayPal", apiRaw<{ status: "not_connected" }>(
    "/v1/admin/integrations/paypal/connect/disconnect", { method: "POST" }, 'disconnectPayPal'));
